import HardwareTicketDataService from '../../../../services/hardware-ticket-activities';

import React, { useState, useEffect, useRef } from 'react';

import {
    Paper,
    Text,
    Table
} from '@mantine/core';

import DashboardDataService from '../../../../services/dashboard-activities';
import { BarChart, AreaChart, PieChart, /* DonutChart */ } from '@mantine/charts';

import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

import { useMantineTheme, useMantineColorScheme } from '@mantine/core';

const itemHeight = 120;

const CustomTooltip = ({ active, payload, label }) => {
    const theme = useMantineTheme();
    if (!active || !payload || payload.length === 0) return null;

    // background adapts to light/dark mode
    const backgroundColor =
        theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white;
    const textColor =
        theme.colorScheme === 'dark' ? theme.colors.gray[0] : theme.black;
    const borderColor =
        theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3];
    const seenNames = new Set();

    return (
      <div
        style={{
          width: 250,            // fixed width
          //maxWidth: 250,         // max width
          backgroundColor,
          border: `1px solid ${borderColor}`,
          borderRadius: 4,
          padding: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          pointerEvents: 'none', // prevents blocking hover interactions
          color: textColor
        }}
      >
        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{label}</div>
        
        {payload.filter((entry) => {
            if (seenNames.has(entry.name)) return false;
            seenNames.add(entry.name);
            return true;
        }).map((entry, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', fontSize: 14, marginBottom: 2 }}>
            <span
                style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: entry.color,
                marginRight: 6,
                }}
            />
            <span style={{display: "flex", justifyContent: 'space-between', flex: 1}}><span>{entry.name}</span> <span>{entry.value}</span></span>
        </div>
        ))}
      </div>
    );
};

// Register required Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).slice(-2);
    }
    return color;
}

// returns the count of overdue tickets by tech and displays in a graph between January 1, 2020 - the current date (to capture all possible tickets)
export default function OverdueCountByTech(props) {
    const [data, setData] = useState([]);
    const [labels, setLabels] = useState([]);
    const [dataValues, setDataValues] = useState([]);
    const [backgroundColors, setBackgroundColors] = useState([]);

    useEffect(() => {
        if (props.loggedStatus.loggedIn) {
            var today = new Date();
            var startDate =  new Date("2020-01-01");
        
            let dateRange = [startDate, today];

            // right now I'm passing 0 for the tenant parameter (show all of them) Kyle's widgets don't currently obey the tenant selector. if we get more tenants, we'll need to change
            // the [0] for assigned tech will return the data for every tech
            HardwareTicketDataService.getOverdueTickets(0, dateRange, "All", [0], props.loggedStatus.id, props.loggedStatus.timezone_adjustment_factor).then(response => {
                console.log("Data returned by overdue count by tech widget was: ", response);
                let processedData = {};

                const counts = response.data.results.reduce((acc, { assigned_tech_name }) => {
                    const key = assigned_tech_name || 'Unassigned'; // treat null as "Unassigned"
                    acc[key] = (acc[key] || 0) + 1;
                    return acc;
                }, {});

                processedData = Object.entries(counts)
                .map(([name, total]) => ({ name, total }))
                .sort((a, b) => a.name.localeCompare(b.name));      // sort by tech name
                //.sort((a, b) => b.total - a.total);               // sort by ticket count

                if(processedData !== undefined && processedData !== null) {
                    setLabels(processedData.map((item) => item.name));
                    setDataValues(processedData.map((item) => item.total));
                    setBackgroundColors(processedData.map((item) => stringToColor(item.name)));
                }
                

                console.log("Overdue counts by tech object is: ", processedData);
                setData(processedData);

            })
            .catch(e => {
                console.log(e);
            });
        }
    }, []);

    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();

    const chartData = {
        labels,
        datasets: [
          {
            label: "Overdue Tickets",
            data: dataValues,
            backgroundColor: backgroundColors,
            borderColor: backgroundColors,
            borderWidth: 1,
          },
        ],
      };
    
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
                display: false
                /* position: "right",
                labels: {
                    // Use a function to set label color dynamically
                    color: colorScheme === 'dark' ? theme.colors.gray[0] : theme.black,
                    font: {
                    size: 14,
                    weight: '500',
                    },
                }, */
            }
        },
      };

    return (
        <Paper withBorder radius="md" h="100%" w="100%" style={{ verticalAlign: "top", display: "flex", flexDirection: "column", overflow: "hidden", }}>
            <Text p="20px">Overdue Ticket Count By Technician</Text>
            <div
                style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-evenly",
                gap: "2rem",
                flexWrap: "wrap",
                flex: 1, // make this section take remaining height
                overflow: "hidden", // contain children
                }}
            >
                {/* Doughnut Chart */}
                <div
                style={{
                    width: 500,
                    height: props.widget.size.y * itemHeight - 90,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
                >
                    <Doughnut data={chartData} options={options} />
                </div>

                {/* Scrollable Table */}
                <div
                style={{
                    flex: 1,
                    maxWidth: 500,
                    height: props.widget.size.y * itemHeight - 90,
                    overflowY: "auto",
                }}
                >
                <Table.ScrollContainer minWidth={400}>
                    <Table>
                    <Table.Thead>
                        <Table.Tr>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Total</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {data &&
                        data.map((tech, idx) => (
                            <Table.Tr key={`${tech.name}-${idx}`}>
                            <Table.Td>{tech.name}</Table.Td>
                            <Table.Td>{tech.total}</Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
                </div>
            </div>
            {/* <div style={{ 
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-evenly",
                    gap: "2rem",
                    flexWrap: "wrap", // optional: allows wrapping on smaller screens
            }}>
                <div style={{ 
                    width: 500, 
                    height: props.widget.size.y * itemHeight - 90,
                    // display: 'flex',
                    // justifyContent: 'center', // centers horizontally
                    // alignItems: 'center',     // centers vertically
                    // margin: '0 auto',         // centers in parent if width is less than parent
                }}>
                    <Doughnut data={chartData} options={options} />
                </div>
                <div style={{ flex: 1, maxWidth: 500 }}>
                    <Table.ScrollContainer minWidth={500} maxHeight={300}>
                        <Table>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Name</Table.Th>
                                    <Table.Th>Total</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {data && data.map((tech, idx) => (
                                    <Table.Tr key={`${tech.name}-${idx}`}>
                                        <Table.Td>{tech.name}</Table.Td>
                                        <Table.Td>{tech.total}</Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                </div>
            </div> */}
            {/* <DonutChart 
                withLabelsLine  
                withLabels 
                data={data}
                labelsType="value"
            /> */}
            {/* <BarChart
                h={props.widget.size.y * itemHeight - 90}
                m={0}
                pr="20px"
                data={data}
                dataKey="name"
                //orientation='vertical'
                //yAxisProps={{ width: 80 }}
                series={[{ name: 'total', color: 'blue.6' }]}
                tickLine="y"
                withTooltip
                tooltipProps={{
                    content: <CustomTooltip />,  // use custom tooltip
                    cursor: { stroke: 'rgba(128,128,128,0.6)', strokeWidth: 1 }, // hover line
                }}
            /> */}
        </Paper>
    )

}